import { ChevronsUpDown, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  findNodeById,
  type GicsTreeNode,
  IndustryBrowserDialog,
  type SelectedIndustry,
} from "@/lib/industry";

interface Props {
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  treeData: GicsTreeNode[];
}

export default function IndustrySelector({
  value,
  onChange,
  disabled,
  treeData,
}: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const currentLabel = useMemo(() => {
    if (!value) return null;
    const node = findNodeById(treeData, `sub_industry-${value}`);
    return node?.name ?? null;
  }, [value, treeData]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  const handleSelect = (industry: SelectedIndustry) => {
    const numericId = parseInt(industry.id.replace("sub_industry-", ""), 10);
    onChange(numericId);
  };

  return (
    <>
      <div className="relative w-full">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => setIsDialogOpen(true)}
          className="w-full justify-between font-normal pr-16"
        >
          {currentLabel ? (
            <span className="truncate">{currentLabel}</span>
          ) : (
            <span className="text-muted-foreground">Select industry...</span>
          )}
        </Button>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-muted"
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </div>
      </div>

      <IndustryBrowserDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSelect={handleSelect}
        currentValue={
          value
            ? {
                id: `sub_industry-${value}`,
                name: currentLabel || "",
                level: 4,
              }
            : null
        }
        treeData={treeData}
        selectableLevels={[4]}
        title="Select Sub-Industry"
        description="Navigate through the GICS hierarchy and select a sub-industry"
      />
    </>
  );
}
